import React, { Component } from "react";
import Strings from "@my-dld/assets/strings";
import { MollakDefendant, RVSCompany, RVSLookUp } from "@eres/rvsapi/entities";
import { I8N, LocaleUtil } from "@eres/core";
import { FixedMobileInput } from "../FixedEidInput";
import TextInputField from "../Form/TextInputField";
import PickerInput from "../Form/PickerInput";
import CompanySearchPopup from "./CompanySearchPopup";
import { JQModal } from "../Popup/Utils";
import { isEmailAddress, isEmptyStringOrNull, normaliseMobileNumber } from "@eres/core/dist/StringUtils";
import { showErrorMessage } from "@my-dld/utils/UIUtils";

interface Props {
	defendantTypes: RVSLookUp[];
	mollakDefendant?: MollakDefendant;
	complainTypeID?: number;
}

export interface DefendantDetailsState {
	company?: RVSCompany;
	defendantMobile?: string;
	defendantEmail?: string;
	defendantType?: RVSLookUp;
}

interface State extends DefendantDetailsState {
	error: { [ k: string ]: string };
	showSearchPopup?: boolean;
	mollakDefendant?: MollakDefendant;
	companyDisable?: boolean;
	mobileDisable?: boolean;
	emailDisable?: boolean;
	typeDisable?: boolean;
}

class DefendantDetails extends Component<Props, State> {
	static COMPLAIN_TYPE_OWNER_ASSOCIATION_COMPANY = 21;
	static OWNER_ASSOCIATION_COMPANY = 7;

	constructor( props: Props ) {
		super( props );
		this.state = {
			error: {},
		};
	}

	UNSAFE_componentWillReceiveProps( nextProps: Readonly<Props>, nextContext: any ) {
		const oldDetails = JSON.stringify( this.props.mollakDefendant );
		const newDetails = JSON.stringify( nextProps.mollakDefendant );
		if ( nextProps && ( this.props.complainTypeID !== nextProps.complainTypeID || oldDetails !== newDetails ) ) {
			this.setState( {
				...this.state,
				...stateFromProps( nextProps, this.state.defendantType ),
				error: {},
			} );
		}
	}

	render() {
		const { defendantTypes, complainTypeID } = this.props;
		const {
			error,
			company,
			showSearchPopup,
			companyDisable,
			mobileDisable,
			emailDisable,
			typeDisable,
		} = this.state;
		return <React.Fragment>
			<div className="card" id="DefendantDetails">
				<div className="card-header">
					<span>{ "3 - " + Strings.defendant_details }</span>
				</div>
				<div className="card-body">
					<div className="row">
						<div className="col-md-12">
							<div className="mandatory-field-info">
								<small>{ Strings.mandatory_fields_note }</small>
							</div>
						</div>
						<div className="col-md-6 form-group">
							<label>{ Strings.company_license } <span className="asterisk">*</span></label>
							<div className="input-group">
								<input
									type="text"
									className={ "form-control ltr-text-field " + ( company ? "valid-validation" : "empty-validation" ) }
									readOnly={ true }
									placeholder={ Strings.select_company_to_register_complain }
									value={ company ? LocaleUtil.getLocalValue( company ) : "" }/>
								<div className="input-group-append">
									<button
										className="btn btn-grey" type="button" onClick={ this.onSearchCompany }
										disabled={ companyDisable }><i className="fa fa-search"/>
									</button>
								</div>
							</div>
						</div>
						<div className="col-md-6">
							<FixedMobileInput
								id={ "defendantMobile" }
								holder={ this }
								label={ Strings.Mobile_text }
								error={ error.defendantMobile }
								containerClassName={ "form-group field-style" }
								disabled={ mobileDisable }/>
						</div>
						<div className="col-md-6">
							<TextInputField
								id={ "defendantEmail" }
								holder={ this }
								label={ Strings.email }
								error={ error.defendantEmail }
								containerClassName={ "form-group field-style" }
								disabled={ emailDisable }/>
						</div>
						<div className="col-md-6">
							<PickerInput
								id={ "defendantType" }
								holder={ this }
								required={ true }
								label={ Strings.defendant_type }
								items={ defendantTypes }
								formatter={ ( item: RVSLookUp ) => I8N.isRTL() ? item.NameAr : item.NameEn }
								toIdentifier={ ( item: RVSLookUp ) => item.ID + "" }
								error={ error.defendantType }
								disabled={ typeDisable }/>
						</div>
					</div>
				</div>
			</div>
			{ showSearchPopup && complainTypeID && <CompanySearchPopup
				complainTypeID={ complainTypeID }
				onClose={ this.onClose }
				onCompanySelect={ this.onCompanySelect }/> }
		</React.Fragment>;
	}

	public isValid(): Promise<DefendantDetailsState> {
		let isValid = true;
		const { company, defendantMobile, defendantEmail, defendantType } = this.state;
		const error: { [ k: string ]: string } = {};
		if ( company == undefined ) {
			error.company = Strings.required;
			isValid = false;
		}
		let mobile;
		if ( defendantMobile && !isEmptyStringOrNull( defendantMobile ) ) {
			mobile = normaliseMobileNumber( defendantMobile );
			if ( mobile && mobile !== "971" && mobile.length !== 12 ) {
				error.defendantMobile = Strings.invalid_mobile_number;
				isValid = false;
			}
		}
		if ( !isEmptyStringOrNull( defendantEmail ) && ( defendantEmail && !isEmailAddress( defendantEmail ) ) ) {
			error.defendantEmail = Strings.invalid_email_address;
			isValid = false;
		}
		if ( defendantType == undefined ) {
			error.defendantType = Strings.required;
			isValid = false;
		}
		this.setState( {
			error: error,
		} );
		if ( isValid ) {
			return Promise.resolve( {
				...this.state,
				...{ defendantMobile: ( mobile === "971" ) ? undefined : mobile }
			} );
		}
		return Promise.reject( "DefendantDetails not Valid" );
	}

	public reset() {
		this.setState( {
			company: undefined,
			defendantMobile: "",
			defendantEmail: "",
			defendantType: undefined,
			companyDisable: undefined,
			mollakDefendant: undefined,
			mobileDisable: undefined,
			emailDisable: undefined,
			typeDisable: undefined,
			error: {},
		} );
	}

	private onSearchCompany = () => {
		if ( this.props.complainTypeID !== undefined ) {
			this.setState( { showSearchPopup: true } );
		} else {
			showErrorMessage( Strings.rvs_company_search_val_msg );
		}
	};

	private onClose = () => {
		JQModal( "company_search_popup" ).hide();
		this.setState( {
			showSearchPopup: false,
		} );
	};

	private onCompanySelect = ( company: RVSCompany ) => {
		JQModal( "company_search_popup" ).hide();
		this.setState( {
			company: company,
			showSearchPopup: false,
		} );
	};
}

function stateFromProps( props: Props, sType?: RVSLookUp ) {
	const details: MollakDefendant | undefined = props.mollakDefendant;
	let mobile = ( details && details.mobile ) ? details.mobile : "";
	mobile = mobile ? normaliseMobileNumber( mobile ) : "";
	let email = ( details && details.email ) ? details.email : "";
	email = isEmailAddress( email ) ? email : "";
	const isAssociationComp = ( props.complainTypeID === DefendantDetails.COMPLAIN_TYPE_OWNER_ASSOCIATION_COMPANY );
	const type = ( details || isAssociationComp ) ? props.defendantTypes.find( ( type: RVSLookUp ) =>
		type.ID === DefendantDetails.OWNER_ASSOCIATION_COMPANY ) : sType;
	const newState: Partial<State> = {
		company: details ? details.company : undefined,
		defendantMobile: mobile,
		defendantEmail: email,
		defendantType: type,
		companyDisable: ( details !== undefined && details.company !== undefined ),
		mobileDisable: !isEmptyStringOrNull( email ),
		emailDisable: !isEmptyStringOrNull( mobile ),
		typeDisable: isAssociationComp,
	};
	return newState;
}

export default DefendantDetails;
